/**
 * useUsageTracking Hook
 * React hook for tracking feature usage and limits
 */

import { useState, useEffect, useCallback } from 'react';
import UsageTrackingService from '../services/usageTrackingService';

interface UsageInfo {
  used: number;
  limit: number | null;
  remaining: number;
  canUse: boolean;
}

interface UseUsageTrackingResult {
  aiChat: UsageInfo;
  arScan: UsageInfo;
  isLoading: boolean;
  incrementAIChat: () => Promise<boolean>;
  incrementARScan: () => Promise<boolean>;
  refresh: () => Promise<void>;
}

const defaultUsageInfo: UsageInfo = {
  used: 0,
  limit: null,
  remaining: -1,
  canUse: true
};

export const useUsageTracking = (): UseUsageTrackingResult => {
  const [aiChat, setAIChat] = useState<UsageInfo>(defaultUsageInfo);
  const [arScan, setARScan] = useState<UsageInfo>(defaultUsageInfo);
  const [isLoading, setIsLoading] = useState(true);

  const usageService = UsageTrackingService.getInstance();

  const loadUsage = async () => {
    setIsLoading(true);
    try {
      const summary = await usageService.getUsageSummary();
      const [aiChatCheck, arScanCheck] = await Promise.all([
        usageService.canUseFeature('ai_chat'),
        usageService.canUseFeature('ar_scan')
      ]);

      setAIChat({
        used: summary.aiChat.used,
        limit: summary.aiChat.limit,
        remaining: summary.aiChat.remaining,
        canUse: aiChatCheck.allowed
      });

      setARScan({
        used: summary.arScan.used,
        limit: summary.arScan.limit,
        remaining: summary.arScan.remaining,
        canUse: arScanCheck.allowed
      });
    } catch (error) {
      console.error('[useUsageTracking] Error loading usage:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const incrementAIChat = useCallback(async (): Promise<boolean> => {
    const success = await usageService.incrementUsage('ai_chat');
    if (success) {
      await loadUsage();
    }
    return success;
  }, []);

  const incrementARScan = useCallback(async (): Promise<boolean> => {
    const success = await usageService.incrementUsage('ar_scan');
    if (success) {
      await loadUsage();
    }
    return success;
  }, []);

  const refresh = useCallback(async () => {
    await loadUsage();
  }, []);

  useEffect(() => {
    loadUsage();
  }, []);

  return {
    aiChat,
    arScan,
    isLoading,
    incrementAIChat,
    incrementARScan,
    refresh
  };
};
